import * as React from "react";
import { Column } from "primereact/column";
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, Button } from "reactstrap";
import { Link } from "react-router-dom";
import PageLayout from "../../components/PageLayout";
import useEntitiesList from "../../utils/listHookNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataTable } from "primereact/datatable";
import { getDateTime, renderHeader, filterDateTime, DropdownFilterElement } from "../../components/TableUtils";
import Can from "../../auth/Can";
import roles from "../../auth/roles";
import { useAuth0 } from "../../auth/react-auth0-wrapper";
import classNames from 'classnames';
import { deleteSale } from "../../api/sales";

export default function SalesList({ opt, activeTenant }) {
    const { domains, user } = useAuth0();
    let dtable;
    const createdBy = user.isInRole([roles.VENDITORE, "AGENT-AS-SELLER"], activeTenant) ? `AND createdBy='${user.email}'` : ``

    const [result, loading, setLoading, globalFilter, setGlobalFilter, forceRefresh] = useEntitiesList("sells", "createdat desc", `tenant_id='${activeTenant._id}' ${createdBy}`, "");

    const [openModal, setOpenModal] = React.useState(false);
    const toggle = () => setOpenModal(!openModal);

    const onDelete = async id => {
        const value = await deleteSale(id);
        toggle();
        forceRefresh();
        return value;
    };

    const modalSection = () => (
        <Modal zIndex={2000} centered size="sm" isOpen={openModal ? true : false} backdrop={true}>
            <ModalHeader toggle={toggle}>
                Eliminazione Vendita
        </ModalHeader>
            <ModalBody>
                <p>Confermi di voler eliminare la Vendita?</p>
            </ModalBody>
            <ModalFooter>
                <Button color="link" className="btn-link-dark" onClick={toggle}>Annulla</Button>{' '}
                <Button color="danger" className="ml-auto" onClick={() => onDelete(openModal._id)}>Elimina</Button>
            </ModalFooter>
        </Modal>
    );

    const getHeader = () => {
        return (
            <Can
                roles={[roles.DEALER, roles.VENDITORE, roles.ADMIN, "AGENT-AS-SELLER"]}
                yes={() =>
                    <div className="action-container">
                        <Link
                            title="Aggiungi vendita"
                            className="btn btn-primary p-button-icon-only"
                            to={`sales-add`}
                        >
                            <FontAwesomeIcon icon="hand-holding-usd" className="mr-1" />Nuova Vendita
                        </Link>
                    </div>
                }
                no={() => null}
            />
        );
    };

    const onRefresh = () => {
        forceRefresh();
        setGlobalFilter("");
    }

    const exportTable = () => {
        dtable.exportCSV();
    }

    return (
        <PageLayout title={`Elenco Vendite - Dealer: ${activeTenant.name}`}>
            <Row>
                <Col>
                    <DataTable
                        ref={el => {
                            dtable = el;
                        }}
                        loading={loading}
                        value={result}
                        rowsPerPageOptions={[5, 10, 20, 50, 100]}
                        className="p-datatable-hoverable-rows p-datatable-customers"
                        responsive
                        paginator={true}
                        autoLayout={false}
                        first={0}
                        rows={20}
                        totalRecords={result.length}
                        paginatorPosition="both"
                        header={renderHeader(loading, result.length, undefined, getHeader(), { globalFilter, setGlobalFilter, onRefresh }, exportTable)}
                        globalFilter={globalFilter}
                        emptyMessage={() => (<div className="text-center">Nessuna vendita in tabella</div>)}
                        paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                        currentPageReportTemplate="Visualizzati {first} di {last} Vendite su un totale di {totalRecords}"
                    >
                        <Column
                            field="attributes.created_for"
                            header="Cliente"
                            sortable
                            filter
                            filterMatchMode="contains"
                            style={{ wordWrap: "break-word" }}
                            body={(rowData, column) => {
                                return (
                                    <Link
                                        to={`/tenant/sales/${rowData._id}`}
                                    >
                                        {rowData.attributes.created_for}
                                    </Link>
                                );
                            }}
                        />
                        <Column
                            field="createdby"
                            header="Eseguita da"
                            sortable
                            filter={user.isInRole([roles.VENDITORE, "AGENT-AS-SELLER"], activeTenant) ? false : true}
                            filterMatchMode="contains"
                            style={{ wordWrap: "break-word" }}
                        />
                        <Column
                            field="attributes.type"
                            style={{ wordWrap: "break-word" }}
                            header="Tipo"
                            sortable
                            body={el => domains.devicesType.find(item => el.attributes && item.value === el.attributes.type).label}
                            filter
                            filterElement={
                                <DropdownFilterElement
                                    field="attributes.type"
                                    options={domains.devicesType}
                                    onChange={e => dtable.filter(e.value, e.field)}
                                />
                            }
                        />
                        <Column
                            field="attributes.quantity"
                            header="Quantitá"
                            sortable
                            filter
                            style={{ wordWrap: "break-word" }}
                        />
                        <Column
                            field="createdat"
                            header="Creato il"
                            sortable
                            filter
                            body={getDateTime}
                            filterMatchMode="custom"
                            filterFunction={filterDateTime}
                            style={{ wordWrap: "break-word" }}
                        />
                        <Column
                            field="status"
                            header="Stato"
                            sortable
                            filter
                            style={{ wordWrap: "break-word", textAlign: "center" }}
                            filterElement={
                                <DropdownFilterElement
                                    field="status"
                                    options={[
                                        { label: "inserito", value: "inserito" },
                                        { label: "chiuso", value: "chiuso" }
                                    ]}
                                    onChange={e => dtable.filter(e.value, e.field)}
                                />
                            }
                            body={(rowData, column) => {
                                return (
                                    <span className={classNames('customer-badge', 'status-' + rowData.status)}>{rowData.status}</span>
                                );
                            }}
                        />

                        <Column
                            field="actions"
                            body={(rowData, column) => {
                                return (
                                    <div className="action-container">
                                        <Link
                                            title="Gestisci vendita"
                                            className="btn btn-sm btn-primary p-button-icon-only"
                                            to={`/tenant/sales/${rowData._id}`}
                                        >
                                            <FontAwesomeIcon icon="pencil-alt" />
                                        </Link>
                                        {rowData.meta.product.needsAuto.toString() === "true" && (
                                            <Link
                                                title="Riepilogo veicoli e collaudi"
                                                className="btn btn-sm btn-first p-button-icon-only"
                                                to={`/tenant/sales/doc/${rowData._id}`}
                                            >
                                                <FontAwesomeIcon icon="cogs" />
                                            </Link>
                                        )}
                                        <Can
                                            roles={[roles.ADMIN]}
                                            yes={() => (
                                                <Button
                                                    color="danger"
                                                    title="Elimina vendita"
                                                    onClick={() => setOpenModal(rowData)}
                                                    className="btn-sm p-button-icon-only"
                                                >
                                                    <FontAwesomeIcon icon="trash-alt" />
                                                </Button>
                                            )}
                                            no={() => null}
                                        />
                                    </div>
                                );
                            }}
                            style={{ wordWrap: "break-word" }}
                        />
                    </DataTable>
                </Col>
            </Row>
            {modalSection()}
        </PageLayout>
    );
}
